import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { UserCheck, Eye, X, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { loadAssignmentUsers } from '../lib/assignmentUsers';
import { AssignmentRemovalDialog } from './AssignmentRemovalDialog';
import { canManageAssignments } from '../lib/assetPermissions';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const ROLES = [
  { key: 'gestor', field: 'gestores', label: 'Gestor', color: 'bg-blue-100 text-blue-700', Icon: UserCheck },
  { key: 'mediador', field: 'mediadores', label: 'Mediador', color: 'bg-emerald-100 text-emerald-700', Icon: UserCheck },
  { key: 'inversor', field: 'inversores', label: 'Inversor', color: 'bg-amber-100 text-amber-700', Icon: Eye },
];

const peopleFor = (asset, field, directory) => {
  const metadata = new Map(directory.map(user => [String(user.id), user]));
  (asset.assigned_users || []).forEach(user => metadata.set(String(user.id), user));
  const people = new Map();
  (asset[field] || []).forEach(raw => {
    const user = metadata.get(String(raw));
    const id = user?.canonical_id || user?.id || String(raw);
    people.set(id, { id, name: user?.name || user?.email || 'Usuario no disponible' });
  });
  return [...people.values()];
};

export const AssetAssignments = ({ asset, canEdit: allowEdit, currentUser, onRefresh, onAccessLost }) => {
  const canEdit = !!allowEdit && canManageAssignments(currentUser);
  const [directory, setDirectory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [retry, setRetry] = useState(0);
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    if (!canEdit) { setDirectory([]); return; }
    const controller = new AbortController();
    setLoading(true);
    setLoadError('');
    loadAssignmentUsers(controller.signal).then(items => { if (!controller.signal.aborted) setDirectory(items); })
      .catch(() => { if (!controller.signal.aborted) setLoadError('No se pudo cargar la lista de usuarios.'); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [canEdit, retry]);

  const save = async (role, person, action) => {
    if (busy || !canEdit) return;
    setBusy(true);
    setError('');
    try {
      const { data } = await axios.post(`${API}/assets/${asset.id}/assignments`, {
        action, funcion: role.key, user_id: person.id, confirm_remove: action === 'remove',
      }, { withCredentials: true });
      setPending(null);
      const message = data.assigned ? `${person.name} añadido a ${role.label}`
        : data.removed_assignments ? `${person.name} retirado de ${role.label}` : 'La asignación ya estaba actualizada';
      toast.success(<span data-testid="assignment-detail-result">{message}</span>, { id: 'assignment-detail-result' });
      if (!data.can_view) { onAccessLost(); return; }
      await onRefresh();
    } catch (e) {
      setError(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'No se pudo guardar la asignación.');
    } finally { setBusy(false); }
  };
  if (!canEdit && !ROLES.some(role => (asset[role.field] || []).length)) return null;
  return (
    <section className="mt-4 pt-4 border-t border-slate-100" data-testid="asset-assignments">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {ROLES.map(role => {
          const people = peopleFor(asset, role.field, directory);
          if (!canEdit && !people.length) return null;
          const assigned = new Set(people.map(person => person.id));
          const options = directory.filter(person => !assigned.has(person.id));
          return <div key={role.key} className="min-w-0 space-y-2" data-testid={`header-${role.field}`}>
            <div className="flex items-center gap-2 text-xs text-slate-500" data-testid={`assignment-label-${role.key}`}><role.Icon className="w-4 h-4" />{role.label}</div>
            <div className="flex flex-wrap gap-1.5 items-center">
              {people.map(person => <span key={person.id} className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full max-w-full min-w-0 font-medium ${role.color}`} data-testid={`assignment-chip-${role.key}-${person.id}`}>
                <span className="min-w-0 [overflow-wrap:anywhere]" data-testid={`assignment-name-${role.key}-${person.id}`}>{person.name}</span>
                {canEdit && <button type="button" disabled={busy} title={`Quitar a ${person.name} de ${role.label}`} aria-label={`Quitar a ${person.name} de ${role.label}`}
                  onClick={() => { setError(''); setPending({ role, person }); }}
                  className="shrink-0 p-1 rounded-full hover:bg-black/10 transition-colors disabled:opacity-40"
                  data-testid={`assignment-remove-${role.key}-${person.id}`}><X className="w-3 h-3" /></button>}
              </span>)}
              {!people.length && <span className="text-xs text-slate-400" data-testid={`assignment-empty-${role.key}`}>Sin asignados</span>}
            </div>
            {canEdit && <select value="" disabled={busy || loading || !!loadError || !options.length}
              onChange={e => { const person = directory.find(item => item.id === e.target.value); if (person) save(role, { id: person.id, name: person.name || person.email }, 'assign'); }}
              className="input-base text-xs w-full min-w-0 max-w-full" aria-label={`Añadir usuario a ${role.label}`} data-testid={`assignment-add-${role.key}`}>
              <option value="">{busy ? 'Guardando…' : loading ? 'Cargando usuarios…' : !options.length && !loadError ? 'Sin usuarios disponibles' : 'Añadir usuario…'}</option>
              {options.map(person => <option key={person.id} value={person.id}>{`${person.name || person.email}${person.name && person.email ? ' · ' + person.email : ''}`}</option>)}
            </select>}
          </div>;
        })}
      </div>
      {loadError && canEdit && <div className="flex flex-wrap items-center gap-2 mt-3 text-xs text-red-700" role="alert" data-testid="assignment-users-error">
        <span>{loadError}</span><button type="button" onClick={() => setRetry(n => n + 1)} disabled={busy} className="flex items-center gap-1 underline" data-testid="assignment-users-retry"><RefreshCw className="w-3 h-3" />Reintentar</button>
      </div>}
      {error && !pending && <p className="mt-3 text-sm text-red-700" role="alert" data-testid="assignment-detail-error">{error}</p>}
      {canEdit && pending && <AssignmentRemovalDialog person={pending.person} roleLabel={pending.role.label}
        selfRemoval={currentUser?.role === 'gestor' && pending.person.id === currentUser?.id}
        busy={busy} error={error} onCancel={() => { setPending(null); setError(''); }}
        onConfirm={() => save(pending.role, pending.person, 'remove')} />}
    </section>
  );
};